<script setup lang="ts">
  import { computed, ref } from 'vue'
  import type { Component } from 'vue'
  import draggable from 'vuedraggable'
  import {
    ClipboardPlus,
    ClipboardList,
    ClipboardCheck,
    ClipboardX,
    Clock4,
    X,
    StickyNote
  } from 'lucide-vue-next'
  import { TooltipProvider, TooltipTrigger, TooltipContent, Tooltip } from '@/components/ui/tooltip'
  import DocumentStatusBadge from '@/components/app/cases/documents/DocumentStatusBadge.vue'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { useRouter } from 'vue-router'
  import { DocumentStatusEnum, type Document } from '@/types/generated'
  import Badge from '@/components/ui/badge/Badge.vue'
  import UserAvatar from '../users/UserAvatar.vue'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'

  const props = defineProps<{
    items: any[]
    loading: boolean
    t: Function
    onStatusChange: Function
  }>()

  const router = useRouter()

  type ColumnItem = any // Replace 'any' with your actual case item type if available

  type Column = {
    title: string
    items: ColumnItem[]
    icon: Component
    bgColor: string
    borderColor: string
    textColor: string
    iconColor: string
    status: string
    count: number
  }

  type ColumnGroups = {
    NEW: Column
    IN_PROGRESS: Column
    COMPLETED: Column
    REJECTED: Column
  }

  const columns = computed<ColumnGroups>(() => {
    const grouped: ColumnGroups = {
      NEW: {
        title: 'Neue Anfrage',
        items: [],
        icon: ClipboardPlus,
        bgColor: 'bg-yellow-100',
        borderColor: 'border-yellow-300',
        textColor: 'text-yellow-700',
        iconColor: 'text-yellow-600',
        status: 'NEW',
        count: 0
      },
      IN_PROGRESS: {
        title: 'In Bearbeitung',
        items: [],
        icon: ClipboardList,
        bgColor: 'bg-blue-100',
        borderColor: 'border-blue-300',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-600',
        status: 'IN_PROGRESS',
        count: 0
      },
      COMPLETED: {
        title: 'Abgeschlossen',
        items: [],
        icon: ClipboardCheck,
        bgColor: 'bg-green-100',
        borderColor: 'border-green-300',
        textColor: 'text-green-700',
        iconColor: 'text-green-600',
        status: 'COMPLETED',
        count: 0
      },
      REJECTED: {
        title: 'Abgelehnt',
        items: [],
        icon: ClipboardX,
        bgColor: 'bg-red-100',
        borderColor: 'border-red-300',
        textColor: 'text-red-700',
        iconColor: 'text-red-600',
        status: 'REJECTED',
        count: 0
      }
    }

    // Reset all column items
    Object.values(grouped).forEach((column) => {
      column.items = []
    })

    // Group items by state
    props.items.forEach((item) => {
      if (grouped[item.state as keyof typeof grouped]) {
        grouped[item.state as keyof typeof grouped].items.push(item)
      }
    })

    // Add badge count
    Object.values(grouped).forEach((column) => {
      column.count = column.items.length
    })

    return grouped
  })

  const selectedDocumentStatus = ref<DocumentStatusEnum | null>(null)
  const documentStatuses = [
    DocumentStatusEnum.REQUESTED,
    DocumentStatusEnum.RECEIVED,
    DocumentStatusEnum.COMPLETED
  ]

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return (
      date
        .toLocaleString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
        .replace(',', ' um') + ' Uhr'
    )
  }

  const getDocumentCountByStatus = (documents: Document[], status: DocumentStatusEnum): number => {
    return documents.filter((doc) => doc.document_status === status).length
  }

  const navigateToCaseDetailDocuments = (caseUuid: string) => {
    router.push(`/app/cases/${caseUuid}/documents`)
  }

  const hasNewActivity = (lastActivity: string, lastActivitySeen: string) => {
    if (!lastActivity || !lastActivitySeen) return false
    return new Date(lastActivity) > new Date(lastActivitySeen)
  }

  const handleAdd = (e: any) => {
    props.onStatusChange(e.item.__draggable_context.element, e.to.dataset.columnStatus)
  }

  const navigateToCaseDetail = (caseUuid: string) => {
    router.push(`/app/cases/${caseUuid}`)
  }
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Skeleton Loading UI -->
    <div
      v-if="loading"
      class="grid h-full grid-cols-4 gap-4 p-4"
    >
      <div
        v-for="columnType in ['NEW', 'IN_PROGRESS', 'COMPLETED', 'REJECTED']"
        :key="columnType"
        class="flex flex-col"
      >
        <!-- Column Header Skeleton -->
        <div class="sticky top-0 z-10 mb-3">
          <div
            :class="{
              'border-yellow-300 bg-yellow-100': columnType === 'NEW',
              'border-blue-300 bg-blue-100': columnType === 'IN_PROGRESS',
              'border-green-300 bg-green-100': columnType === 'COMPLETED',
              'border-red-300 bg-red-100': columnType === 'REJECTED'
            }"
            class="border-rounded rounded-lg border p-2"
          >
            <div class="flex items-center gap-2">
              <SkeletonLoader class="h-5 w-5 rounded" />
              <SkeletonLoader class="h-4 w-24 rounded" />
              <SkeletonLoader class="h-5 w-6 rounded-full" />
            </div>
          </div>
        </div>

        <!-- Content Container Skeleton -->
        <div class="h-[calc(100vh-8rem)] overflow-hidden rounded-lg bg-gray-100 py-2">
          <!-- Cards Area Skeleton -->
          <div class="h-full space-y-3 overflow-y-auto overflow-x-hidden px-4 py-2">
            <!-- Generate 3 skeleton cards per column -->
            <div
              v-for="i in 3"
              :key="i"
              class="w-full rounded-lg border bg-white p-4 text-left shadow-sm"
            >
              <div class="space-y-3">
                <!-- Header -->
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <SkeletonLoader class="h-2 w-2 rounded-full" />
                    <SkeletonLoader class="h-4 w-32 rounded" />
                  </div>
                </div>

                <!-- Content -->
                <SkeletonLoader class="h-3 w-full rounded" />
                <SkeletonLoader class="h-3 w-3/4 rounded" />

                <!-- User Info -->
                <div class="flex items-center gap-2">
                  <SkeletonLoader class="h-6 w-6 rounded-full" />
                  <SkeletonLoader class="h-4 w-24 rounded" />
                </div>

                <!-- Footer -->
                <div class="flex items-center justify-between">
                  <SkeletonLoader class="h-5 w-24 rounded" />
                  <SkeletonLoader class="h-5 w-6 rounded-md" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Kanban Board -->
    <div class="grid h-full grid-cols-4 gap-4 p-4">
      <div
        v-for="(column, status) in columns"
        :key="status"
        class="flex flex-col"
      >
        <!-- Column Header -->
        <div class="sticky top-0 z-10 mb-3">
          <div
            :class="[column.bgColor, column.borderColor, column.textColor]"
            class="border-rounded rounded-lg border p-2"
          >
            <div class="flex items-center gap-2">
              <component
                :is="column.icon"
                :class="column.iconColor"
                class="h-5 w-5"
              />
              <span class="text-sm font-medium">{{ column.title }} ({{ column.count }})</span>
            </div>
          </div>
        </div>

        <!-- Content Container -->
        <div class="h-[calc(100vh-8rem)] overflow-hidden rounded-lg bg-gray-100 py-2">
          <!-- Cards Area -->
          <draggable
            :list="column.items"
            :group="{ name: 'cases', pull: true, put: true }"
            :data-column-status="column.status"
            item-key="uuid"
            class="h-full space-y-3 overflow-y-auto overflow-x-hidden px-4 py-2"
            :status="status"
            @add="handleAdd"
          >
            <template #item="{ element: caseItem }">
              <button
                class="w-full rounded-lg border bg-white p-4 text-left shadow-sm hover:bg-gray-50"
                @click="navigateToCaseDetail(caseItem.uuid)"
              >
                <div class="space-y-3">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <div
                        v-if="
                          hasNewActivity(caseItem.last_activity_at, caseItem.last_activity_seen_at)
                        "
                        class="relative flex h-2 w-2"
                        :title="formatDateTime(caseItem.last_activity_at)"
                      >
                        <span
                          class="absolute inline-flex h-full w-full animate-ping rounded-full bg-purple-400 opacity-75"
                        ></span>
                        <span
                          class="relative inline-flex h-2 w-2 rounded-full bg-purple-500 shadow-[0_0_10px_2px_rgba(139,70,255,0.3)]"
                        ></span>
                      </div>
                      <h4 class="font-medium">
                        {{ caseItem.data?.client?.first_name }}
                        {{ caseItem.data?.client?.last_name }}
                      </h4>
                    </div>
                  </div>

                  <div class="truncate text-sm text-gray-600">
                    <template v-if="caseItem.data.law.field || caseItem.data.law.topic">
                      <span>{{ caseItem.data.law.field && caseItem.data.law.field + '/' }}</span>
                      <span>{{ caseItem.data.law.topic || '' }}</span>
                    </template>
                    <span v-else>-</span>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <div class="flex items-center text-sm text-gray-500">
                          <Clock4 class="mr-2 size-4 min-w-4" />
                          {{ formatDateTime(caseItem.modified_at) }}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        variant="secondary"
                        showArrow
                      >
                        <div class="flex flex-col gap-1 text-sm">
                          <div class="flex items-center">
                            <strong>{{ t('updated') }}:</strong>
                            <span class="ml-1">{{ formatDateTime(caseItem.modified_at) }}</span>
                          </div>
                          <div class="flex items-center">
                            <strong>{{ t('created') }}:</strong>
                            <span class="ml-1">
                              {{ formatDateTime(caseItem.created_at || '') }}
                            </span>
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <div class="">
                    <div
                      v-if="caseItem.clerk"
                      class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3"
                    >
                      <div
                        class="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500 text-sm text-white"
                      >
                        {{ caseItem.clerk.first_name.charAt(0) }}
                      </div>
                      <span class="text-sm">
                        {{ caseItem.clerk.first_name + ' ' + caseItem.clerk.last_name }}
                      </span>
                    </div>
                    <div
                      v-else
                      class="text-sm text-gray-500"
                    >
                      Sachbearbeiter nicht zugewiesen
                    </div>
                  </div>
                  <div class="flex items-center justify-between">
                    <Popover v-if="caseItem.documents?.length">
                      <PopoverTrigger @click.stop>
                        <div class="flex cursor-pointer items-center gap-1">
                          <template
                            v-for="status in documentStatuses"
                            :key="status"
                          >
                            <DocumentStatusBadge
                              v-if="getDocumentCountByStatus(caseItem.documents, status) > 0"
                              :type="status"
                              :count="getDocumentCountByStatus(caseItem.documents, status)"
                              @click="selectedDocumentStatus = status"
                            />
                          </template>
                        </div>
                      </PopoverTrigger>
                      <PopoverContent
                        class="p-4"
                        :align="'start'"
                      >
                        <div class="flex flex-col items-start space-y-3">
                          <div class="flex w-full items-center justify-between">
                            <div
                              v-if="selectedDocumentStatus"
                              class="flex items-center gap-2"
                            >
                              <span class="text-sm font-medium">
                                {{
                                  getDocumentCountByStatus(
                                    caseItem.documents,
                                    selectedDocumentStatus
                                  )
                                }}
                                {{
                                  getDocumentCountByStatus(
                                    caseItem.documents,
                                    selectedDocumentStatus
                                  ) === 1
                                    ? t('document')
                                    : t('documents')
                                }}
                              </span>
                              <DocumentStatusBadge
                                :type="selectedDocumentStatus"
                                showText
                              />
                            </div>
                            <X
                              class="size-5 cursor-pointer text-gray-500 hover:text-gray-700"
                              @click="selectedDocumentStatus = null"
                            />
                          </div>
                          <a
                            href="#"
                            class="mt-2 text-center text-purple-600 underline hover:text-purple-800"
                            @click.stop="navigateToCaseDetailDocuments(caseItem.uuid || '')"
                          >
                            {{ t('viewAllDocuments') }}
                          </a>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <span v-else>
                      {{ t('noDocuments') }}
                    </span>
                    <Popover>
                      <PopoverTrigger
                        v-if="caseItem.note"
                        @click.stop
                      >
                        <Badge
                          variant="secondary"
                          class="flex items-center gap-1 rounded-md p-1 font-medium text-gray-500"
                          :class="caseItem.note ? 'hover:brightness-95' : ''"
                        >
                          <StickyNote class="size-4" />
                          {{ '1' }}
                        </Badge>
                      </PopoverTrigger>
                      <PopoverContent v-if="caseItem.note">
                        <div
                          v-if="caseItem.clerk"
                          class="flex items-center gap-2 pb-2"
                        >
                          <UserAvatar
                            :user="caseItem.clerk"
                            size="32px"
                          />
                          <div class="flex flex-col">
                            <span class="text-md font-medium">
                              {{ caseItem.clerk.first_name }} {{ caseItem.clerk.last_name }}
                            </span>
                            <span class="text-sm text-gray-500">{{ caseItem.clerk.email }}</span>
                          </div>
                        </div>
                        <div class="flex flex-col gap-1 text-sm">
                          {{ caseItem.note }}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </button>
            </template>
          </draggable>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .sortable-ghost {
    opacity: 0.5;
    background: #f7f7f7;
  }

  .sortable-drag {
    opacity: 0.9;
    transform: rotate(2deg);
    cursor: grabbing;
  }

  /* Custom scrollbar styling */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
</style>
